import { useCallback, useEffect, useMemo, useState } from "react";
import { useLanguage } from "../../../../../stores/LanguageStore";
import {
  Box,
  Button,
  CloseButton,
  Group,
  Image,
  LoadingOverlay,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconChevronLeft,
  IconChevronRight,
  IconCopy,
  IconFile,
} from "@tabler/icons-react";
import StepMapping from "../../../../protected/lists/import/manualImport/StepMapping";
import MappingStats from "../../../../protected/lists/import/manualImport/MappingStats";
import system_map from "../../../../protected/lists/import/manualImport/default_system_fields.json";
import prm_map from "../../../../protected/lists/import/manualImport/default_prm_fields.json";
import system_map_blacklist from "../../../../protected/lists/import/manualImport/default_system_fields_blacklist.json";
import StepImportSettings from "../../../../protected/lists/import/manualImport/StepImportSettings";
import { useConfigStore } from "../../../../../stores/ConfigStore";
import { executeSequentially, getYouTubeMaxThumbnail } from "../../../../../functions/utilFunctions";
import {
  createList,
  enrichList,
  importContacts,
  importPrmContacts,
} from "../../../../../api/ContactAPI";
import StepCopyPaste from "../../../../protected/lists/import/manualImport/StepCopyPaste";
import { modals } from "@mantine/modals";
import { useProfileStore, useSessionStore } from "../../../../../stores/UserStore";
import HowItWorks from "../../../../protected/lists/pages/HowItWorks";
import StepImportFile from "./StepImportFile";
import VideoModalViewer from "../../../../../components/View/VideoModalViewer";
import { PlayIcon } from "../../../../../components/Icons/SvgIcons";
import manual_import_fr from "../../../../../assets/list-import/manual_import_fr.png";
import manual_import_en from "../../../../../assets/list-import/manual_import_en.png";
import StepPreviewUnsub from "./StepPreviewUnsub";

const contactsLimit = 200000;

const ManualImport = ({
  onFinish,
  listProfile,
  listId,
  isActive,
  setActiveWindow,
  isBlacklist,
  isUnsub,
  isPrm,
}) => {
  const lang = useLanguage((s) => s.language);
  const [activeStep, setActiveStep] = useState(1);
  const reseller = useSessionStore((s) => s.reseller);
  const hasVideo = reseller?.videos?.find(
    (x) => x.name === "ListsManualImportTutorial"
  );
  const notReseller = reseller?.name === "Magileads";

  const video1 = reseller?.videos?.find(
    (x) => x.name === "ListsManualImportTutorial"
  )?.url;

  const defaultFields = useMemo(() => {
    let result = {};

    if (isBlacklist) {
      Object.keys(system_map_blacklist).forEach((key) => {
        system_map[key].forEach((value) => {
          result[value] = key;
        });
      });
    } else {
      const datafields = useConfigStore.getState().datafields;
      datafields.forEach((df) => {
        if (!df.readonly) result[df.name] = df.identifier_placeholder;
      });

      Object.keys(system_map).forEach((key) => {
        system_map[key].forEach((value) => {
          result[value] = key;
        });
      });

      if (isPrm) {
        Object.keys(prm_map).forEach((key) => {
          prm_map[key].forEach((value) => {
            result[value] = key;
          });
        });
      }
    }

    return result;
  }, [isBlacklist]);

  const [files, setFiles] = useState([]);
  const [data, setData] = useState(null);
  const [sheetNames, setSheetNames] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [merge, setMerge] = useState(false);
  const [fields, setFields] = useState(defaultFields);
  const [consent, setConsent] = useState(true);
  const [importing, setImporting] = useState(false);
  const [headerLine, setHeaderLine] = useState(1);
  const [lines, setLines] = useState([1, 0]);

  const handleNext = useCallback(() => {
    if (
      isBlacklist &&
      Object.values(fields)
        .filter(Boolean)
        .some((x) => !["%linkedin_url%", "%email%"].includes(x))
    ) {
      return modals.openConfirmModal({
        title: "Attention",
        onConfirm: () => setActiveStep((prev) => prev + 1),
        confirmProps: {
          children: lang.import_contacts.manual.mapping_alert_continue,
          color: "red",
        },
        cancelProps: {
          children: lang.global.cancel,
        },
        children: <Text>{lang.import_contacts.manual.mapping_alert}</Text>,
      });
    }

    setActiveStep((prev) => prev + 1);
    setShouldWarn?.(true);
  }, [fields, isBlacklist, lang]);

  const handlePrev = () => {
    setActiveStep((prev) => prev - 1);
  };

  // Copy-paste
  const [pastedText, setPastedText] = useState("");
  const [separator, setSeparator] = useState("0");

  const getNextDisabled = () => {
    if (activeStep === 1) {
      return !files.length || data?.length === 0 || !consent;
    }
    if (activeStep === 2) {
      if (isUnsub) {
        return false
      }
      let statFields = {};
      Object.keys(fields || {}).forEach((field) => {
        if (data) {
          if (Object.keys(data?.[0] || {})?.includes(field)) {
            statFields = { ...statFields, [field]: fields[field] };
          }
        }
      });
      return Object.values(statFields).filter((x) => !!x).length === 0;
    }
    return false;
  };

  const handleImport = useCallback(
    async ({ name, ignoreContactErrors, enrich, list }) => {
      setImporting(true);
      const datafields = useConfigStore.getState().datafields;
      const parsedFields = {};
      Object.keys(data[0]).forEach((key) => {
        if (fields[key] === "id") {
          parsedFields[key] = "id";
        } else {
          parsedFields[key] = datafields.find(
            (x) => x.identifier_placeholder === fields[key]
          )?.id;
        }
      });

      let contacts = data.map((contact) => {
        let props = [];
        let otherParams = {};
        let contact_id;

        Object.keys(contact).forEach((column) => {
          if (contact[column]) {
            if (parsedFields[column]) {
              if (parsedFields[column] === "id" && parseInt(contact[column])) {
                contact_id = parseInt(contact[column]);
              } else {
                props.push({
                  data_field_id: parsedFields[column],
                  value: contact[column]?.toString(),
                });
              }
            } else {
              const areInt = [
                "score",
                "amount",
                "probability",
                "custom_status",
              ];
              otherParams[column] = areInt.includes(column)
                ? parseInt(contact[column])
                : contact[column];
            }
          }
        });

        if (isPrm) {
          if (otherParams?.created_on) {
            delete otherParams.created_on;
          }
          if (otherParams?.tags) {
            delete otherParams.tags;
          }
          otherParams.custom_status = otherParams?.custom_status || null;
          return {
            id: contact_id,
            properties: props,
            ...otherParams,
          };
        }

        return {
          id: contact_id,
          properties: props,
        };
      });

      // Divide requests
      async function mainFunction(id) {
        let requests = [];
        const numberOfUploads = Math.ceil(contacts.length / contactsLimit);

        const importFunction = (slicedData) => {
          let formData = new FormData();
          var file = new Blob([JSON.stringify(slicedData)], {
            type: "application/json",
          });
          formData.append("contacts", file, "data.json");
          formData.append("ignore_errors", ignoreContactErrors);

          if (isPrm) {
            return importPrmContacts(formData);
          }
          return importContacts(id, formData);
        };

        for (let i = 0; i < numberOfUploads; i++) {
          // const contactsToUpload = Math.min(contactsLimit, (totalContacts.length) - (i * contactsLimit));
          const sliced = contacts.slice(
            i * contactsLimit,
            (i + 1) * contactsLimit
          );

          requests.push(() => importFunction(sliced));
        }
        return executeSequentially(requests)
          .catch((err) => {
            // let state_message = err.response.data?.state_message;
            // if (state_message === "data_validation_exception") {
            //   setErrors(err.response.data.errors);
            // } else if (
            //   state_message === "multiple_contact_info_already_exist"
            // ) {
            //   setErrors(err.response.data.errors);
            // } else if (state_message === "import_limit_exceeded") {
            //   setExceeded(true);
            // }
            // return setExceeded(false);
          })
          .then(() => {
            setShouldWarn(false);

            if (enrich) {
              enrichList({ contact_list_id: id, generate_email: true });
            }

            setTimeout(() => {
              onFinish?.(id);
              setImporting(true);
            }, 100);
          })
          .finally(() => {
            setImporting(false);
          });
      }

      // Create a list or import to a specific one
      if (!list?.id && !isPrm) {
        return createList({ name })
          .catch((createError) => {
            let state_message = createError.response.data.state_message;
            // if (state_message === "empty_name") {
            //   return handleErrors(state_message);
            // }
          })
          .then((response) => {
            if (response.data.state) {
              // If list created, import to that list
              mainFunction(response.data.contact_list_id);
            }
          });
      }
      return mainFunction(list?.id);
    },
    [data, fields, onFinish, isPrm]
  );

  const [active, setActive] = useState("file");
  const [shouldWarn, setShouldWarn] = useState(false);
  const tabs = useMemo(() => {
    return [
      {
        label: lang.contact_lists.import.steps_title.file,
        icon: <IconFile size={22} />,
        value: "file",
      },
      {
        label: lang.contact_lists.import.steps_title.copy,
        icon: <IconCopy size={22} />,
        value: "copy",
      },
    ];
  }, [lang]);

  const handleTabChange = (v) => {
    if (shouldWarn)
      modals.openConfirmModal({
        title: "Attention",
        children: (
          <Text size="sm">
            Vos modifications ne seront pas sauvegardés si vous changez de
            méthode d'import.
          </Text>
        ),
        labels: { confirm: lang.global.confirm, cancel: lang.global.cancel },
        onCancel: () => { },
        onConfirm: () => {
          setShouldWarn(false);
          setActive(v);
          setActiveStep(1);
        },
        centered: true,
      });
    else {
      setActive(v);
      setActiveStep(1);
    }
  };

  const getStepComponent = useCallback(() => {
    if (activeStep === 1) {
      if (active === "copy") {
        return (
          <StepCopyPaste
            pastedText={pastedText}
            setPastedText={setPastedText}
            separator={separator}
            setSeparator={setSeparator}
            consent={consent}
            setConsent={setConsent}
            handleNext={handleNext}
            setData={setData}
            isUnsub={isUnsub}
            isBlacklist={isBlacklist}
          />
        );
      }
      return (
        <StepImportFile
          files={files}
          setFiles={setFiles}
          data={data}
          setData={setData}
          consent={consent}
          setConsent={setConsent}
          handleNext={handleNext}
          merge={merge}
          setMerge={setMerge}
          sheetNames={sheetNames}
          setSheetNames={setSheetNames}
          sheets={sheets}
          setSheets={setSheets}
          isLinkedToCampaign={listProfile?.usedInProgrammations?.length > 0}
          lines={lines}
          setLines={setLines}
          headerLine={headerLine}
          setHeaderLine={setHeaderLine}
          isUnsub={isUnsub}
          isBlacklist={isBlacklist}
        />
      );
    }
    if (activeStep === 2) {
      if (isUnsub) {
        return <StepPreviewUnsub
          data={data}
        />
      }
      return (
        <StepMapping
          data={data}
          fields={fields}
          setFields={setFields}
          isBlacklist={isBlacklist}
          isPrm={isPrm}
        />
      );
    }
    if (activeStep === 3) {
      return (
        <StepImportSettings
          files={files}
          data={data}
          fields={fields}
          handlePrev={handlePrev}
          handleImport={handleImport}
          listId={listId}
          setImporting={setImporting}
          onFinish={onFinish}
          isBlacklist={isBlacklist}
          isUnsub={isUnsub}
          isPrm={isPrm}
        />
      );
    }
  }, [
    activeStep,
    active,
    files,
    data,
    consent,
    handleNext,
    merge,
    sheetNames,
    sheets,
    listProfile?.usedInProgrammations?.length,
    lines,
    headerLine,
    pastedText,
    separator,
    fields,
    isBlacklist,
    isUnsub,
    isPrm,
    handleImport,
    listId,
    onFinish,
  ]);

  useEffect(() => {
    const profile = useProfileStore.getState().profile;
    const datafields = useConfigStore.getState().datafields;
    if (profile?.saved_order?.mapping && !isBlacklist && !isPrm) {
      let autoFields = { ...defaultFields };
      let savedMap = profile.saved_order.mapping;

      Object.keys(savedMap).forEach((key) => {
        let df = datafields.find((x) => x.id === parseInt(key));
        if (df) {
          savedMap[key].forEach((value) => {
            let capitalizedKey = value.charAt(0).toUpperCase() + value.slice(1);
            let upperCasedKey = value.toUpperCase();

            let target = df.identifier_placeholder;

            autoFields = {
              ...autoFields,
              [value]: target,
              [capitalizedKey]: target,
              [upperCasedKey]: target,
            };
          });
        }
      });

      setFields(autoFields);
    }
  }, [defaultFields, isBlacklist, isPrm]);

  // useNavigationBlocker(shouldWarn);
  const steps = useMemo(
    () => [
      {
        label:
          active === "copy"
            ? lang.contact_lists.import.steps_title.contacts
            : lang.contact_lists.import.steps_title.file,
        value: "1",
      },
      {
        label: lang.contact_lists.import.steps_title.mapping,
        value: "2",
      },
      {
        label: lang.contact_lists.import.steps_title.importing,
        value: "3",
      },
    ],
    [active, lang]
  );

  const getStepDisabled = useCallback(
    (step) => {
      let v = parseInt(step);
      if (v === 1) {
        return false;
      }
      if (v === 2) {
        return !data;
      }
      if (v === 3) {
        return !data;
      }
      return false;
    },
    [data]
  );

  if (isActive !== "manual") {
    return;
  }

  return (
    <Group wrap="nowrap" gap={"xl"}>

      <Paper
        component={Stack}
        p={isPrm ? 0 : "lg"}
        pt={8}
        style={{
          boxShadow: "#00000012 0 2px 6px",
        }}
        // className="animate__animated animate__fadeIn animate__faster"
        mb={isBlacklist || isUnsub ? 32 : 0}
        flex={"1 1 auto"}
      >
        {setActiveWindow && (
          <CloseButton onClick={() => setActiveWindow("def")} mb={-16} />
        )}
        {isUnsub ? (
          <Group justify="center">
            <Title order={4} c={"primary"} fw={500}>
              {lang.import_contacts.manual.title_unsubscribers}
            </Title>
          </Group>
        ) : isBlacklist ? (
          <Group justify="center">
            <Title order={4} c={"primary"} fw={500}>
              {lang.import_contacts.manual.title_blacklists}
            </Title>

            <HowItWorks type={"blacklist"} inside />
          </Group>
        ) : (
          !isPrm && (
            <Group>
              <Title order={4} c={"primary"} fw={500}>
                {lang.import_contacts.manual.title_contacts}
              </Title>

              <HowItWorks type={"file"} inside />
            </Group>
          )
        )}
        <SegmentedControl
          data={tabs.map((item) => ({
            label: (
              <Group key={item.value} justify="center">
                <ThemeIcon variant="subtle" color="gray">
                  {item.icon}
                </ThemeIcon>
                <Text>{item.label}</Text>
              </Group>
            ),
            value: item.value,
          }))}
          value={active}
          onChange={handleTabChange}
          radius={12}
        />
        <Stack
          flex={1}
          pos={"relative"}
          w={"100%"}
          style={{
            borderRadius: 8,
            border: "1px solid var(--mantine-color-primary-0)",
          }}
          p={"lg"}
        >
          <LoadingOverlay visible={importing} overlayProps={{ blur: 1 }} />

          <Group justify="space-between" gap={"lg"}>
            {steps.map((step, i) => {
              let active = step.value === activeStep.toString();
              return (
                <Button
                  key={step.value}
                  variant={active ? "light" : "subtle"}
                  size="md"
                  radius={"md"}
                  color={active ? "primary" : "black"}
                  onClick={() => setActiveStep(parseInt(step.value))}
                  leftSection={
                    <ThemeIcon
                      radius={"md"}
                      variant="outline"
                      size={"sm"}
                      fz={12}
                      color={active ? "primary" : "black"}
                    >
                      {i + 1}
                    </ThemeIcon>
                  }
                  disabled={getStepDisabled(step.value)}
                >
                  {step.label}
                </Button>
              );
            })}
          </Group>

          {getStepComponent()}

          {activeStep !== 3 && (
            <Stack mt={16}>
              {activeStep === 2 && <MappingStats fields={fields} data={data} />}
              {active === "copy" && activeStep === 1 ? (
                ""
              ) : (
                <Group justify="space-between">
                  {activeStep !== 1 && (
                    <Button
                      variant="subtle"
                      leftSection={<IconChevronLeft />}
                      onClick={handlePrev}
                    >
                      {lang.global.back}
                    </Button>
                  )}
                  <Button
                    rightSection={<IconChevronRight />}
                    onClick={handleNext}
                    disabled={getNextDisabled()}
                    ml={"auto"}
                  >
                    {lang.global.next}
                  </Button>
                </Group>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* {(reseller?.name === "Magileads" || hasVideo) && ( */}
      {(reseller?.name === "Magileads" || hasVideo) && !isBlacklist && !isUnsub && (
        <Paper
          component={Stack}
          style={{ boxShadow: "#00000012 0 2px 6px" }}
          p={"md"}
          radius={"md"}
          miw={180}
          w={180}
          h={"100%"}
        >
          <Text mb={16} fw={400} size="sm">
            {lang.import_contacts.manual.text1}
          </Text>

          <Stack flex={"1 1 auto"} h={0} style={{ overflow: "auto" }}>
            {video1 ? (
              <VideoModalViewer
                src={video1}
                opener={(handleOpen) => (
                  <Box
                    span={8}
                    pos={"relative"}
                    className="cover"
                    onClick={handleOpen}
                  >
                    <PlayIcon
                      variant="light"
                      color="primary"
                      size={32}
                      iconSize={32}
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 10,
                        opacity: 0.4,
                      }}
                      radius={32}
                      className="play-button"
                    />

                    {video1.includes("youtube") ? (
                      <Image
                        className="slide-item"
                        src={getYouTubeMaxThumbnail(video1, "ListsManualImportTutorial")}
                        h={"100%"}
                        mih={120}
                        fit="cover"
                      />
                    ) : (
                      <video
                        src={video1}
                        className="slide-item"
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "block",
                        }}
                      />
                    )}
                  </Box>
                )}
              />
            ) : notReseller ? (
              <VideoModalViewer
                src={
                  "https://www.youtube.com/embed/Rf2OzXsnSHM?si=J_JZMzXUJjfXPnFp"
                }
                opener={(handleOpen) => (
                  <Box
                    span={8}
                    pos={"relative"}
                    className="cover"
                    onClick={handleOpen}
                  >
                    <PlayIcon
                      variant="light"
                      color="primary"
                      size={64}
                      iconSize={32}
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 10,
                        opacity: 0.4,
                      }}
                      radius={64}
                      className="play-button"
                    />
                    <Image
                      className="slide-item"
                      src={lang.id === "FRA" ? manual_import_fr : manual_import_en}
                      h={"100%"}
                      mih={120}
                      fit="contain"
                    />
                  </Box>
                )}
              />
            ) : (
              ""
            )}
          </Stack>
        </Paper>
      )}
    </Group>
  );
};

export default ManualImport;
